"use client";

import { AGEventEmitter } from "../events";
import { RtcEvents, IUserTracks } from "./types";
import { apiGenAgoraData } from "../../common";
import { VideoSourceType } from "../../common/constant";

// Dynamically import AgoraRTC only on client side
import AgoraRTC from 'agora-rtc-sdk-ng';
import type { 
  IAgoraRTCClient, 
  IMicrophoneAudioTrack, 
  IRemoteAudioTrack,
  UID
} from 'agora-rtc-sdk-ng';

export class RtcManager extends AGEventEmitter<RtcEvents> {
  private _joined;
  client?: IAgoraRTCClient;
  localTracks: IUserTracks;
  appId: string | null = null;
  token: string | null = null;
  userId: number | null = null;

  constructor() {
    super();
    this._joined = false;
    this.localTracks = {};
    
    // Only initialize Agora client on the browser
    if (typeof window !== 'undefined') {
      try {
        this.client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
        this._listenRtcEvents();
      } catch (error) {
        console.error("Failed to initialize Agora client:", error);
      }
    }
  }

  async join({ channel, userId }: { channel: string; userId: number }) {
    if (!this._joined) {
      const res = await apiGenAgoraData({ channel, userId });
      const { code, data } = res;
      if (code != 0) {
        throw new Error("Failed to get Agora token");
      }
      const { appId, token } = data;
      this.appId = appId;
      this.token = token;
      this.userId = userId;
      await this.client?.join(appId, channel, token, userId);
      this._joined = true;
    }
  }

  async createCameraTracks() {
    try {
      const videoTrack = await AgoraRTC.createCameraVideoTrack();
      this.localTracks.videoTrack = videoTrack;
    } catch (err) {
      console.error("Failed to create video track", err);
    }
    this.emit("localTracksChanged", this.localTracks);
  }

  async createMicrophoneAudioTrack() {
    try {
      // Request specific mic permissions with clear constraints
      const audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        microphoneId: 'default',
        encoderConfig: 'music_standard'
      });
      
      this.localTracks.audioTrack = audioTrack;
      this.emit("localTracksChanged", this.localTracks);
      return audioTrack;
    } catch (err: any) {
      console.error("Failed to create audio track", err);
      
      // Check for specific permission errors
      if (err.code === 'PERMISSION_DENIED' || err.name === 'NotAllowedError') {
        throw new Error("Microphone permission denied. Please allow microphone access in your browser settings.");
      } else if (err.code === 'NOT_READABLE' || err.name === 'NotReadableError') {
        throw new Error("Unable to access microphone. It may be in use by another application.");
      } else {
        throw err;
      }
    }
  }

  async publish() {
    const tracks = [];
    if (this.localTracks.videoTrack) {
      tracks.push(this.localTracks.videoTrack);
    }
    if (this.localTracks.audioTrack) {
      tracks.push(this.localTracks.audioTrack);
    }
    if (tracks.length) {
      await this.client?.publish(tracks);
    }
  }

  async destroy() {
    try {
      if (this.localTracks?.audioTrack) {
        await this.localTracks.audioTrack.stop();
        await this.localTracks.audioTrack.close();
      }
      if (this.localTracks?.videoTrack) {
        await this.localTracks.videoTrack.stop();
        await this.localTracks.videoTrack.close();
      }
      if (this._joined) {
        await this.client?.leave();
      }
    } catch (err) {
      console.error('Error during RTC cleanup:', err);
    } finally {
      this._resetData();
    }
  }

  // ----------- private methods --------------
  private _listenRtcEvents() {
    if (!this.client) {
      console.error("Cannot listen to RTC events: client is not initialized");
      return;
    }
    
    this.client.on("network-quality", (quality) => {
      this.emit("networkQuality", quality);
    });
    this.client.on("user-published", async (user, mediaType) => {
      await this.client?.subscribe(user, mediaType);
      if (mediaType === "audio") {
        this._playAudio(user.audioTrack);
      }
      this.emit("remoteUserChanged", {
        userId: user.uid,
        audioTrack: user.audioTrack,
        videoTrack: user.videoTrack,
      });
    });
    this.client.on("user-unpublished", async (user, mediaType) => {
      await this.client?.unsubscribe(user, mediaType);
      this.emit("remoteUserChanged", {
        userId: user.uid,
        audioTrack: user.audioTrack,
        videoTrack: user.videoTrack,
      });
    });
  }

  private _playAudio(
    audioTrack: IMicrophoneAudioTrack | IRemoteAudioTrack | undefined
  ) {
    if (audioTrack && !audioTrack.isPlaying) {
      audioTrack.play();
    }
  }

  private _resetData() {
    this.localTracks = {};
    this._joined = false;
  }
}

// Only create the rtcManager instance on the client side
let rtcManager: RtcManager;

if (typeof window !== 'undefined') {
  // Ensure AgoraRTC is properly loaded before creating the manager
  if (AgoraRTC) {
    rtcManager = new RtcManager();
  } else {
    console.error("AgoraRTC not available");
    // Create a placeholder that won't break code but won't do anything
    rtcManager = new RtcManager();
  }
} else {
  // Create a placeholder for SSR that won't be used
  rtcManager = null as unknown as RtcManager;
}

export { rtcManager };
